/**
 * The printed front cover, laid out exactly as the print PDF draws it, so every
 * cover preview in the app matches what goes to the print partner.
 *
 * A port of `generateRightCoverPage` in the API's services/printService.js (and
 * its helpers `calculateOptimalFontSize`, utils/helpers/coverTitleWrap.js):
 * the drawing fills the 8.75" × 11.5" page (cropped to fit), the brush-stroke
 * logo band (artlogo1.webp, the same file) sits across the bottom, and the
 * title, upper case in Permanent Marker #C46070, sits on the band, sized
 * between MIN and MAX so it fits 70% of the width in at most 2 lines. Keep the
 * two in step: coverPage.test.js pins numbers taken from the print code.
 *
 * Text is measured in the browser. The print code measures with node-canvas
 * and multiplies every width by 1.03 (meant as a browser correction); with the
 * real font loaded the two measure identically (checked 2026-10-23), so the
 * same 1.03 is applied here to land on the print's size and line breaks. Emoji
 * are measured in the print's emoji font, also like print.
 */

import { forPreview, printableText } from "./printableText";

export const COVER_PAGE = { width: 2625, height: 3450 };
export const COVER_TITLE_COLOR = "#C46070";

const FRONTEND_ASPECT = 7 / 10;
const BACKEND_ASPECT = 8.75 / 11.5;
const MAX_LINES = 2;
const PRINT_WIDTH_FACTOR = 1.03;
const LINE_BREAK = /\r\n|[\r\n\u2028\u2029]/;

/** Fixed geometry of the cover (print pixels). */
export function coverGeometry() {
  const { width: WIDTH, height: HEIGHT } = COVER_PAGE;
  const logoWidth = Math.round(WIDTH * (FRONTEND_ASPECT / BACKEND_ASPECT));
  const logoHeight = Math.round(logoWidth / 3);
  const SCALE_FACTOR = logoWidth / (2550 / 7.08);
  const minFontSize = Math.round(18 * SCALE_FACTOR);
  const maxFontSize = Math.round(24 * SCALE_FACTOR);
  const maxWidthPx = logoWidth * 0.7;
  const paddingPx = Math.round(20 * SCALE_FACTOR);
  const textContentWidth = maxWidthPx - paddingPx;
  const artworkTopY = HEIGHT - logoHeight;
  return {
    width: WIDTH,
    height: HEIGHT,
    logo: { x: Math.round((WIDTH - logoWidth) / 2), y: artworkTopY, width: logoWidth, height: logoHeight },
    minFontSize,
    maxFontSize,
    textContentWidth,
    effectiveWidth: textContentWidth * 0.98,
    artworkCenterY: artworkTopY + logoHeight / 2,
    verticalShift: Math.round(14 * SCALE_FACTOR),
  };
}

/** utils/helpers/coverTitleWrap.js wrapCoverTitle. */
export function wrapCoverTitle(text, measure, maxWidth) {
  const lines = [];
  for (const paragraph of String(text || "").split(LINE_BREAK)) {
    const words = paragraph.split(" ").filter(Boolean);
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && measure(candidate) > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

/**
 * Where the title goes. `measure(line, fontSize)` returns the rendered width
 * of `line` in Permanent Marker (weight 600) at `fontSize` print pixels.
 * Returns { lines, fontSize, firstLineY, lineHeight, strokeWidth }.
 */
export function layoutCoverTitle(title, measure) {
  const g = coverGeometry();
  // Sized and wrapped as it prints; a title with nothing printable counts as no title.
  const displayTitle = printableText(String(title || "").toUpperCase()) || "COLORING BOOK";

  // Customer line breaks are kept only if the title still fits in 2 lines at the smallest size.
  const atMin = (line) => measure(line, g.minFontSize);
  const layoutTitle =
    wrapCoverTitle(displayTitle, atMin, g.effectiveWidth).length <= MAX_LINES ? displayTitle : displayTitle.split(LINE_BREAK).join(" ");

  // calculateOptimalFontSize: the largest size from MAX down that fits in 2 lines.
  const linesAt = (size) => wrapCoverTitle(layoutTitle, (line) => measure(line, size), g.effectiveWidth).length;
  let fontSize = g.minFontSize;
  if (!layoutTitle.trim() || linesAt(g.maxFontSize) === 1) {
    fontSize = g.maxFontSize;
  } else {
    for (let size = g.maxFontSize; size >= g.minFontSize; size -= 1) {
      if (linesAt(size) <= MAX_LINES) {
        fontSize = size;
        break;
      }
    }
  }

  const lines = wrapCoverTitle(layoutTitle, (line) => measure(line, fontSize), g.effectiveWidth);
  const lineHeight = fontSize;
  const blockHeight = (Math.max(lines.length, 1) - 1) * lineHeight;
  return {
    lines,
    fontSize,
    lineHeight,
    firstLineY: g.artworkCenterY - g.verticalShift - blockHeight / 2,
    strokeWidth: Number((fontSize / 40).toFixed(1)),
  };
}

let measureCtx = null;
/** Browser measurement in Permanent Marker (the font must be loaded; see CoverPreview). */
export function browserMeasure(line, fontSize) {
  if (!measureCtx) {
    if (typeof document === "undefined") return line.length * fontSize * 0.6;
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  if (!measureCtx) return line.length * fontSize * 0.6;
  measureCtx.font = `600 ${fontSize}px "Permanent Marker", "Print Emoji"`;
  return measureCtx.measureText(forPreview(line)).width * PRINT_WIDTH_FACTOR;
}

/**
 * True when the title fits the cover in 2 lines at the smallest size, the rule
 * the live app's title box enforces (TextareaAutoSizeWithLimit refuses a
 * keystroke that would need a third line). Print would otherwise set a longer
 * title on 3 or more lines at the smallest size. Unknown (font not loaded yet)
 * counts as fitting.
 */
export function titleFitsCover(title, measure = browserMeasure) {
  if (measure === browserMeasure && typeof document !== "undefined" && document.fonts && !document.fonts.check('600 16px "Permanent Marker"')) {
    return true;
  }
  const g = coverGeometry();
  const flat = printableText(String(title || "").toUpperCase()).split(LINE_BREAK).join(" ");
  return wrapCoverTitle(flat, (line) => measure(line, g.minFontSize), g.effectiveWidth).length <= MAX_LINES;
}

/** Start loading Permanent Marker so titleFitsCover can measure. */
export function loadCoverFont() {
  if (typeof document !== "undefined" && document.fonts) document.fonts.load('600 100px "Permanent Marker"');
}
